/**
 * /register — minimal create-account form.
 *
 * The first user on a fresh installation becomes platform_owner of
 * a bootstrapped tenant; subsequent registrations are 403 until the
 * S14 invite flow lands. The UI surfaces both outcomes with the
 * same form; the error message is taken from the API.
 */

import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { authApi, type AuthApiError } from "../../auth/api";

const MIN_PASSWORD_LENGTH = 12;

export function Register() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
      );
      return;
    }
    setBusy(true);
    try {
      await authApi.register(email, password, displayName || undefined);
      await refresh();
      // First-user registration grants platform_owner; per ADR-0005,
      // admin-tier roles must enrol MFA before exiting the auth flow.
      // The MfaEnrol page is the next step.
      navigate("/mfa/enrol", { replace: true });
    } catch (exc) {
      const apiError = exc as AuthApiError;
      setError(apiError.message ?? "Registration failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit} noValidate>
        <h1 className="auth-title">Create your account</h1>
        <label className="auth-field">
          <span className="auth-label">Email</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Display name (optional)</span>
          <input
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className="auth-field">
          <span className="auth-label">Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD_LENGTH}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        {error !== null && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}
        <button type="submit" className="auth-primary" disabled={busy}>
          {busy ? "Creating..." : "Create account"}
        </button>
        <div className="auth-links">
          <Link to="/login">Already have an account?</Link>
        </div>
      </form>
    </div>
  );
}
